(function () {
  'use strict';

  const SELECTOR = '[data-nc-city-autocomplete]';
  const FRENCH_COMMUNES_URL = 'https://geo.api.gouv.fr/communes';
  const SEARCH_DELAY = 250;

  function componentValue(component) {
    return component.querySelector('[data-nc-city-combined]')?.value.trim() || '';
  }

  function normalizeCityName(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('fr')
      .trim();
  }

  function initComponent(component) {
    if (component.dataset.ncCityInitialized === 'true') return;
    component.dataset.ncCityInitialized = 'true';

    const input = component.querySelector('[data-nc-city-fallback]');
    const combined = component.querySelector('[data-nc-city-combined]');
    const postal = component.querySelector('[data-nc-city-postal]');
    const normalized = component.querySelector('[data-nc-city-normalized]');
    const country = component.querySelector('[data-nc-city-country]');
    const form = component.closest('form');
    const error = component.parentElement?.querySelector('#' + CSS.escape(component.dataset.errorId || ''));
    const isRequired = component.dataset.required === 'true';

    if (!input || !combined || !postal || !normalized || !country || !form) return;

    const list = document.createElement('div');
    list.className = 'nc-city-autocomplete__postal-list';
    list.id = input.id + '-postal-list';
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', 'Villes et codes postaux disponibles');
    list.hidden = true;
    input.after(list);

    let searchTimer = null;
    let searchController = null;
    let searchRequestId = 0;
    let activeOptionIndex = -1;
    let selected = false;
    let isSearching = false;
    let lastSearchHadOptions = false;

    combined.name = input.name;
    input.removeAttribute('name');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', list.id);
    input.setAttribute('aria-expanded', 'false');

    const clearStructured = () => {
      postal.value = '';
      normalized.value = '';
      country.value = '';
      selected = false;
    };

    const clearError = () => {
      input.classList.remove('is-error');
      input.removeAttribute('aria-invalid');
      input.setCustomValidity('');
      if (error) error.hidden = true;
    };

    const setError = message => {
      input.classList.add('is-error');
      input.setAttribute('aria-invalid', 'true');
      input.setCustomValidity(message);
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
    };

    const hideOptions = () => {
      list.hidden = true;
      list.replaceChildren();
      activeOptionIndex = -1;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    };

    const cancelSearch = () => {
      if (searchTimer) window.clearTimeout(searchTimer);
      searchTimer = null;
      searchController?.abort();
      searchController = null;
      searchRequestId += 1;
      isSearching = false;
    };

    const setSelection = option => {
      cancelSearch();
      const displayValue = option.postalCode + ' ' + option.city;
      input.value = displayValue;
      combined.value = displayValue;
      postal.value = option.postalCode;
      normalized.value = option.city;
      country.value = 'FR';
      selected = true;
      lastSearchHadOptions = false;
      hideOptions();
      clearError();
      input.focus();
    };

    const focusOption = index => {
      const buttons = [...list.querySelectorAll('[data-nc-postal-option]')];
      if (!buttons.length) return;
      activeOptionIndex = Math.min(Math.max(index, 0), buttons.length - 1);
      for (const [buttonIndex, button] of buttons.entries()) {
        const isActive = buttonIndex === activeOptionIndex;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      }
      input.setAttribute('aria-activedescendant', buttons[activeOptionIndex].id);
      buttons[activeOptionIndex].focus();
    };

    const buildOptions = (communes, query, searchedPostalCode) => {
      const normalizedQuery = normalizeCityName(query);
      const exactMatches = searchedPostalCode
        ? communes
        : communes.filter(commune => normalizeCityName(commune.nom) === normalizedQuery);
      const candidates = exactMatches.length ? exactMatches : communes.slice(0, 5);
      const maximumOptions = exactMatches.length ? Number.POSITIVE_INFINITY : 5;
      const seen = new Set();
      const options = [];

      for (const commune of candidates) {
        if (!commune?.nom || !Array.isArray(commune.codesPostaux)) continue;
        const postalCodes = searchedPostalCode ? [searchedPostalCode] : commune.codesPostaux;
        for (const postalCode of postalCodes) {
          if (!commune.codesPostaux.includes(postalCode)) continue;
          const key = postalCode + '|' + commune.nom;
          if (!/^\d{5}$/.test(postalCode) || seen.has(key)) continue;
          seen.add(key);
          options.push({ postalCode, city: commune.nom });
          if (options.length >= maximumOptions) break;
        }
        if (options.length >= maximumOptions) break;
      }

      return options;
    };

    const renderOptions = options => {
      if (!options.length) {
        lastSearchHadOptions = false;
        hideOptions();
        return;
      }

      const fragment = document.createDocumentFragment();
      for (const [index, option] of options.entries()) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'nc-city-autocomplete__postal-option';
        button.id = list.id + '-option-' + index;
        button.dataset.ncPostalOption = String(index);
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', 'false');
        button.textContent = option.postalCode + ' ' + option.city;
        button.addEventListener('click', () => setSelection(option));
        fragment.append(button);
      }

      lastSearchHadOptions = true;
      list.replaceChildren(fragment);
      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };

    const scheduleSearch = () => {
      const query = input.value.trim();
      const postalMatch = query.match(/(?:^|\s)(\d{5})(?:\s|$)/);
      const searchedPostalCode = postalMatch?.[1] || '';
      cancelSearch();
      hideOptions();
      lastSearchHadOptions = false;

      if (!searchedPostalCode && (query.length < 2 || /\d/.test(query))) return;

      const requestId = searchRequestId;
      isSearching = true;
      searchTimer = window.setTimeout(async () => {
        searchController = new AbortController();
        const url = new URL(FRENCH_COMMUNES_URL);
        const parameters = {
          fields: 'nom,codesPostaux',
          boost: 'population',
          limit: '10'
        };
        parameters[searchedPostalCode ? 'codePostal' : 'nom'] = searchedPostalCode || query;
        url.search = new URLSearchParams(parameters);

        try {
          const response = await fetch(url, {
            headers: { Accept: 'application/json' },
            credentials: 'omit',
            signal: searchController.signal
          });
          if (!response.ok) throw new Error('French communes lookup failed.');
          const communes = await response.json();
          if (requestId !== searchRequestId || !Array.isArray(communes)) return;
          renderOptions(buildOptions(communes, query, searchedPostalCode));
        } catch (searchError) {
          if (searchError.name !== 'AbortError' && requestId === searchRequestId) {
            lastSearchHadOptions = false;
            hideOptions();
          }
        } finally {
          if (requestId === searchRequestId) {
            searchController = null;
            isSearching = false;
          }
        }
      }, SEARCH_DELAY);
    };

    input.addEventListener('input', () => {
      combined.value = input.value.trim();
      clearStructured();
      clearError();
      scheduleSearch();
    });
    input.addEventListener('focus', () => {
      if (input.value.trim() && !selected) scheduleSearch();
    });
    input.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        hideOptions();
        return;
      }
      if (event.key !== 'ArrowDown' || list.hidden) return;
      event.preventDefault();
      focusOption(0);
    });

    list.addEventListener('keydown', event => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        focusOption(activeOptionIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (activeOptionIndex === 0) input.focus();
        else focusOption(activeOptionIndex - 1);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        hideOptions();
        input.focus();
      }
    });

    document.addEventListener('pointerdown', event => {
      if (!component.contains(event.target)) hideOptions();
    });

    form.addEventListener('submit', event => {
      const value = input.value.trim();
      combined.value = value;

      if (!value && !isRequired) {
        clearStructured();
        clearError();
        return;
      }
      if (!value) {
        event.preventDefault();
        event.stopImmediatePropagation();
        setError(component.dataset.messageRequired);
        input.focus();
        return;
      }
      if (!selected && (isSearching || lastSearchHadOptions)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        setError(component.dataset.messageSelection);
        input.focus();
        return;
      }
      if (!selected) clearStructured();
      clearError();
    }, true);

    combined.value = input.value.trim();
    component.ncCityValue = () => componentValue(component);
    component.ncCityFocus = () => input.focus();
  }

  function init(root = document) {
    if (root.matches?.(SELECTOR)) initComponent(root);
    for (const component of root.querySelectorAll?.(SELECTOR) || []) initComponent(component);
  }

  window.NCCityAutocomplete = { init, value: componentValue };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
